import Modal, { ModalHeader, ModalBody, ModalFooter, ModalTransition } from '@atlaskit/modal-dialog';
import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { GlobalContext } from '../../common/GlobalContext';
import { AuthenticatedUserCapabilities, CustomFieldType, IdAndValue, LoadingState } from '../../../model/model';
import {
  FieldEditor,
  FieldEditorOptionAction,
  FieldEditorOptionActionCreate,
  FieldEditorOptionActionDelete,
  FieldEditorOptionActionOpenChildren,
  FieldEditorOptionActionRefresh,
  FieldEditorOptionActionReOrderOption,
  FieldEditorOptionActionSetDefault,
  FieldEditorOptionActionSortAlphabetically,
  FieldEditorOptionActionUnSetDefault,
  FieldEditorOptionActionUpdate,
} from '../../stateless/field-editor/FieldEditor';
import Button from '@atlaskit/button';
import {
  UPDATE_CUSTOMFIELD_OPTION,
  CREATE_CUSTOMFIELD_OPTION,
  DELETE_CUSTOMFIELD_OPTION,
  MOVE_CUSTOMFIELD_OPTION,
  DTOCustomFieldOption,
  GET_CUSTOMFIELD_CONTEXT_ALL_OPTIONS,
  RestPaginatedValues,
  RestSingleOptionOfContext,
  OptionDTOMoveOption,
  DTOGetDefaultOption,
  SET_CUSTOMFIELD_DEFAULT_OPTION,
  DTOSetDefaultOption,
} from '../../../generated-common';
import { toIntNullSafe } from '../../../generated-common/util';
import { getLastOptionIdOrNull, isTopLevelOfFieldEditor, listContainsAtLeastOneOption } from '../../../shared-components/util/ui-helper';
import { DefaultOptionHelper } from 'shared-components/stateless/default-option/DefaultOptionHelper';
import { GET_SINGLE_CUSTOMFIELD_OPTION } from 'generated-common/resolver-keys';
export interface OptionValidationErrorForView {
  optionId: string;
  errorMessage: string;
}
export type FieldEditorActionReturnTypeSuccessOrFailure = { status: 'SUCCESS' | 'FAILURE' };
export type FieldEditorActionReturnType = { error: string } | FieldEditorActionReturnTypeSuccessOrFailure | null;

// a little function to help us with reordering the result
export const reorderOptionsTransient = (list: any[], startIndex: number, endIndex: number): any[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

export const FieldEditorStateful = ({
  customFieldId,
  contextId,
  fieldType,
  defaultOption,
  setDefaultOption,
  onOptionDeletedForDefaultOptionUpdate,
  parentOptionIds,
  authenticatedUserCapabilities,
}: {
  customFieldId: string;
  contextId: number;
  fieldType: CustomFieldType;
  defaultOption: DTOGetDefaultOption;
  setDefaultOption: Dispatch<SetStateAction<DTOGetDefaultOption>>;
  onOptionDeletedForDefaultOptionUpdate: (parentIds: string[] | null, option: DTOCustomFieldOption) => void;
  parentOptionIds: string[] | null /* Empty list or parentOptionId list from parent to last child */;
  authenticatedUserCapabilities: AuthenticatedUserCapabilities;
}) => {
  const [defaultOptionEnriched, setDefaultOptionEnriched] = useState<DTOGetDefaultOption>({});

  const [postServerSideSortedTrigger, setPostServerSideSortedTrigger] = useState<'ping' | 'pong'>('ping');
  const togglePostServerSideSortedTrigger = () => setPostServerSideSortedTrigger((prev) => (prev === 'ping' ? 'pong' : 'ping'));

  const [customFieldOptionsLoadingState, setCustomFieldOptionsLoadingState] = useState(LoadingState.NOT_LOADED);
  const [isDeletingOptionIdOrNull, _setIsDeletingOptionIdOrNull] = useState<string | null>(null);
  const [isUpdatingOptionIdOrNull, _setIsUpdatingOptionIdOrNull] = useState<string | null>(null);

  const [sortOptionsLoadingState, _setSortOptionsLoadingState] = useState(LoadingState.NOT_LOADED);

  // const [customFieldOptions, setCustomFieldOptions] = useState<CustomFieldOption[]>([]);

  const [customFieldOptions, setCustomFieldOptions] = useState<RestPaginatedValues<RestSingleOptionOfContext> | null>(null);
  const { invoke, t, handleRestError } = useContext(GlobalContext);

  const [allOptionIdsAndValuesLowerCase, setAllOptionIdsAndValuesLowerCase] = useState<IdAndValue[]>([]);

  const [isChildOptionsModalOpen, setIsChildOptionsModalOpen] = useState(false);
  const [childOptionsModalParentOptionId, setChildOptionsModalParentOptionId] = useState<string | null>(null);

  // Note: This must be here on top of all. Since once the request happened, the whole option list is updated and the components
  //       inside the list are rerendered and/or unmounted. Therefore we need to keep this state up here.
  const [validationErrorFromServer, _setValidationErrorFromServer] = useState<OptionValidationErrorForView | null>(null);

  const modalCloseText = t('global.modalCloseFieldEditorLevel');

  const _parentOptionIdTemp = getLastOptionIdOrNull(parentOptionIds);
  const parentOptionIdOrUndefined = _parentOptionIdTemp ? toIntNullSafe(_parentOptionIdTemp) : undefined;

  if (false) {
    // KEEP CODE BUILDING - NEVER USED!!!!
    _setSortOptionsLoadingState(LoadingState.NOT_LOADED);
    _setValidationErrorFromServer(null);
  }

  useEffect(() => {
    //
    // Note: We get the defaultOptions with only optionIds from backend.
    //  - The normal single select, multiselect option values can be enriched from the later loaded options
    //  - ONE EXCEPTION: The cascading child option value must be loaded lazily via REST request if present!
    //
    // Note: We have to make sure this effect is only triggered in the following events
    //  (1) Only on highest parent level => isTopLevelOfFieldEditor()
    //  (2) Only once BOTH defaultOption AND options are loaded (Two different requests!) => listContainsAtLeastOneOption() + defaultOption will be loaded once component is loaded
    //  (3) For the second+n time something changes e.g. defaulOption change or added option => hasChangeDefaultOptionByIdForUseEffect()
    //
    let isSubscribed = true;
    if (
      isTopLevelOfFieldEditor(parentOptionIds) &&
      listContainsAtLeastOneOption(customFieldOptions) &&
      DefaultOptionHelper.hasChangeDefaultOptionByIdForUseEffect(defaultOption, defaultOptionEnriched)
    ) {
      if (defaultOption.defaultChildOption && defaultOption.defaultChildOption.optionId) {
        console.log('DEFAULT OPTION CHANGE: invoke GET_SINGLE_CUSTOMFIELD_OPTION for cascading default option');
        invoke(GET_SINGLE_CUSTOMFIELD_OPTION, {
          fieldId: customFieldId,
          contextId: toIntNullSafe(contextId),
          optionId: toIntNullSafe(defaultOption.defaultChildOption.optionId),
        }).then((resp: any) => {
          if (isSubscribed) {
            if (resp && resp.hasError === true) {
              handleRestError(resp, 'GET_SINGLE_CUSTOMFIELD_OPTION');
            } else {
              const enrichedDefaultOption = DefaultOptionHelper.enrichOptionValuesOnExistingDefaultOptionsObjectWithValuePlaceholders(defaultOption, fieldType, customFieldOptions!.values);
              enrichedDefaultOption.defaultChildOption!.optionValue = resp.payload.value;
              setDefaultOptionEnriched(enrichedDefaultOption);
            }
          }
        });
      } else {
        console.log('DEFAULT OPTION CHANGE: enriching default options without additional API call');
        // FOR ALL OTHER FIELD TYPES => VALUES CAN BE RETRIEVED VIA EXISTING LOADED CUSTOMFIELD OPTIONS
        const enrichedDefaultOption = DefaultOptionHelper.enrichOptionValuesOnExistingDefaultOptionsObjectWithValuePlaceholders(defaultOption, fieldType, customFieldOptions!.values);
        if (isSubscribed) {
          setDefaultOptionEnriched(enrichedDefaultOption);
        }
      }
    }
    return () => {
      // cancel the subscription
      isSubscribed = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOption, customFieldOptions]);

  useEffect(() => {
    let isSubscribed = true;

    setCustomFieldOptionsLoadingState(LoadingState.LOADING);
    invoke(GET_CUSTOMFIELD_CONTEXT_ALL_OPTIONS, {
      fieldId: customFieldId,
      contextId: toIntNullSafe(contextId),
      parentOptionId: parentOptionIdOrUndefined,
    }).then((resp: any) => {
      if (isSubscribed) {
        if (resp && resp.hasError === true) {
          setCustomFieldOptionsLoadingState(LoadingState.FAILED);
          handleRestError(resp, 'GET_CUSTOMFIELD_CONTEXT_ALL_OPTIONS');
        } else {
          setCustomFieldOptions(resp.payload);
          setCustomFieldOptionsLoadingState(LoadingState.LOADED);
        }
      }
    });
    return () => {
      // cancel the subscription
      isSubscribed = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFieldId, contextId, postServerSideSortedTrigger]);

  useEffect(() => {
    // Recalculate lowerCase options on every change of setCustomFieldOptions()
    setAllOptionIdsAndValuesLowerCase((_prev) => {
      const next: IdAndValue[] = [];
      if (customFieldOptions && customFieldOptions.values) {
        for (const o of customFieldOptions.values) {
          if (o && o.id && o.value) {
            next.push(new IdAndValue(o.id, o.value.toLowerCase()));
          }
        }
      }
      return next;
    });
  }, [customFieldOptions]);

  const handleUpdateOption = (action: FieldEditorOptionAction): Promise<FieldEditorActionReturnType> => {
    // We never reject here!
    return new Promise((res) => {
      if (action instanceof FieldEditorOptionActionReOrderOption) {
        const actionTyped: FieldEditorOptionActionReOrderOption = action;
        setCustomFieldOptionsLoadingState(LoadingState.LOADING);
        const moveObj: OptionDTOMoveOption = {
          fieldId: customFieldId,
          contextId: toIntNullSafe(contextId),
          parentOptionId: parentOptionIdOrUndefined,
          move: { id: toIntNullSafe(actionTyped.option.id), after: undefined, position: undefined },
        };
        // REORDER JSON FOR REST API
        if (action.destinationIndex === 0) {
          // Check Special Case#1 first in list
          moveObj.move.position = 'First';
        } else {
          // Check Special Case#2 somewhere else
          // It depends if it was dargged-bottom-up or top-down!
          let moveAfterIndex: number;
          if (action.destinationIndex > action.sourceIndex) {
            // DOWN
            moveAfterIndex = action.destinationIndex;
          } else {
            // UP
            moveAfterIndex = action.destinationIndex - 1;
          }
          const afterOption = allOptionIdsAndValuesLowerCase[moveAfterIndex];
          moveObj.move.after = toIntNullSafe(`${afterOption.id}`);
        }
        // REORDER IN UI ONLY
        setCustomFieldOptions((prev: any) => {
          const temp: RestPaginatedValues<RestSingleOptionOfContext> = { ...prev };
          temp.values = reorderOptionsTransient(prev!.values, action.sourceIndex, action.destinationIndex);
          return temp;
        });

        // REQUEST
        invoke(MOVE_CUSTOMFIELD_OPTION, moveObj).then((ret: any) => {
          setCustomFieldOptionsLoadingState(LoadingState.LOADED);
          if (ret && ret.hasError) {
            handleRestError(ret, 'MOVE_CUSTOMFIELD_OPTION');
            res({ status: 'FAILURE' });
          } else {
            res({ status: 'SUCCESS' });
            // UPDATE OPTION IN LIST
            if (customFieldOptions && customFieldOptions.values && ret && ret.payload && ret.payload.options && ret.payload.options[0]) {
              const tempOptions = { ...customFieldOptions };
              tempOptions.values.push(ret.payload.options[0]);
              setCustomFieldOptions(tempOptions);
            }
          }
        });
      }
      if (action instanceof FieldEditorOptionActionCreate) {
        setCustomFieldOptionsLoadingState(LoadingState.LOADING);
        invoke(CREATE_CUSTOMFIELD_OPTION, {
          fieldId: customFieldId,
          contextId: toIntNullSafe(contextId),
          parentOptionId: parentOptionIdOrUndefined,
          create: {
            value: action.option.value,
            disabled: action.option.disabled,
          },
        }).then((ret: any) => {
          setCustomFieldOptionsLoadingState(LoadingState.LOADED);
          if (ret && ret.hasError) {
            handleRestError(ret, 'CREATE_CUSTOMFIELD_OPTION');
            res({ status: 'FAILURE' });
          } else {
            res({ status: 'SUCCESS' });
            // UPDATE OPTION IN LIST
            if (customFieldOptions && customFieldOptions.values && ret && ret.payload && ret.payload.options && ret.payload.options[0]) {
              const tempOptions = { ...customFieldOptions };
              tempOptions.values.push(ret.payload.options[0]);
              setCustomFieldOptions(tempOptions);
            }
          }
        });
      }
      if (action instanceof FieldEditorOptionActionUpdate) {
        setCustomFieldOptionsLoadingState(LoadingState.LOADING);
        _setIsUpdatingOptionIdOrNull(action.option.id);
        invoke(UPDATE_CUSTOMFIELD_OPTION, {
          fieldId: customFieldId,
          contextId: toIntNullSafe(contextId),
          parentOptionId: parentOptionIdOrUndefined,
          update: {
            id: toIntNullSafe(action.option.id),
            value: action.option.value,
            disabled: action.option.disabled,
          },
        }).then((ret: any) => {
          setCustomFieldOptionsLoadingState(LoadingState.LOADED);
          if (ret && ret.hasError) {
            handleRestError(ret, 'UPDATE_CUSTOMFIELD_OPTION');
            res({ status: 'FAILURE' });
            _setIsUpdatingOptionIdOrNull(null);
          } else {
            res({ status: 'SUCCESS' });
            // UPDATE OPTION IN LIST
            if (customFieldOptions && customFieldOptions.values) {
              const tempOptions = { ...customFieldOptions };
              const maybeOption = tempOptions.values.find((co) => `${co.id}` === `${action.option.id}`);
              if (maybeOption) {
                maybeOption.value = action.option.value;
                maybeOption.disabled = action.option.disabled;
                setCustomFieldOptions(tempOptions);
              }
            }
            _setIsUpdatingOptionIdOrNull(null);
          }
        });
      }
      if (action instanceof FieldEditorOptionActionDelete) {
        setCustomFieldOptionsLoadingState(LoadingState.LOADING);
        _setIsDeletingOptionIdOrNull(action.option.id ? action.option.id : null);
        invoke(DELETE_CUSTOMFIELD_OPTION, {
          fieldId: customFieldId,
          contextId: toIntNullSafe(contextId),
          parentOptionId: parentOptionIdOrUndefined,
          delete: {
            id: toIntNullSafe(action.option.id),
          },
        }).then((ret: any) => {
          setCustomFieldOptionsLoadingState(LoadingState.LOADED);
          if (ret && ret.hasError) {
            handleRestError(ret, 'DELETE_CUSTOMFIELD_OPTION');
            res({ status: 'FAILURE' });
            _setIsDeletingOptionIdOrNull(null);
          } else {
            res({ status: 'SUCCESS' });
            // UPDATE OPTION IN LIST
            if (customFieldOptions && customFieldOptions.values) {
              const tempOptions = { ...customFieldOptions };
              const values = tempOptions.values.filter((co) => `${co.id}` !== `${action.option.id}`);
              if (values) {
                tempOptions.values = values;
                setCustomFieldOptions(tempOptions);
              }
            }
            onOptionDeletedForDefaultOptionUpdate(parentOptionIds, action.option);

            _setIsDeletingOptionIdOrNull(null);
          }
        });
      }
      if (action instanceof FieldEditorOptionActionUnSetDefault) {
        setCustomFieldOptionsLoadingState(LoadingState.LOADING);
        invoke(SET_CUSTOMFIELD_DEFAULT_OPTION, {
          fieldId: customFieldId,
          contextId: toIntNullSafe(contextId),
          doFullDelete: true,
          optionId: undefined,
          optionIds: undefined,
          childOptionId: undefined,
        } as DTOSetDefaultOption).then((ret: any) => {
          setCustomFieldOptionsLoadingState(LoadingState.LOADED);
          if (ret && ret.hasError) {
            handleRestError(ret, 'SET_CUSTOMFIELD_DEFAULT_OPTION');
            res({ status: 'FAILURE' });
          } else {
            setDefaultOption(() => ({} as DTOGetDefaultOption));
            res({ status: 'SUCCESS' });
          }
        });
      }
      if (action instanceof FieldEditorOptionActionSetDefault) {
        setCustomFieldOptionsLoadingState(LoadingState.LOADING);
        invoke(SET_CUSTOMFIELD_DEFAULT_OPTION, {
          fieldId: customFieldId,
          contextId: toIntNullSafe(contextId),
          doFullDelete: false,
          optionId: action.defaultOption.optionId,
          optionIds: action.defaultOption.optionIds,
          childOptionId: action.defaultOption.childOptionId,
        } as DTOSetDefaultOption).then((ret: any) => {
          setCustomFieldOptionsLoadingState(LoadingState.LOADED);
          if (ret && ret.hasError) {
            handleRestError(ret, 'SET_CUSTOMFIELD_DEFAULT_OPTION');
            res({ status: 'FAILURE' });
          } else {
            setDefaultOption((_prev: DTOGetDefaultOption) => DefaultOptionHelper.generateGetDefaultOptionObjectFromSetDefaultOptionObject(action.defaultOption));
            res({ status: 'SUCCESS' });
          }
        });
      }
      if (action instanceof FieldEditorOptionActionOpenChildren) {
        if (action.option.id) {
          setChildOptionsModalParentOptionId(action.option.id);
          setIsChildOptionsModalOpen(true);
        }
      }
      if (action instanceof FieldEditorOptionActionSortAlphabetically) {
        /*
        setSortOptionsLoadingState(LoadingState.LOADING);
        CustomFieldService.sortAlphabetically(errorContext, globals, action, customFieldId, contextId, getLastOptionIdOrNull(parentOptionIds))
          .then(() => {
            setSortOptionsLoadingState(LoadingState.LOADED);
            togglePostServerSideSortedTrigger();
            res(null);
          })
          .catch((error: any) => {
            setSortOptionsLoadingState(LoadingState.LOADED);
            res(error);
          });
          */
      }
      if (action instanceof FieldEditorOptionActionRefresh) {
        togglePostServerSideSortedTrigger();
        setCustomFieldOptionsLoadingState(LoadingState.LOADING);
      }
    });
  };

  const getOptionNameById = (id: string) => {
    if (customFieldOptions && customFieldOptions.values) {
      const opt = customFieldOptions.values.find((op) => `${op.id}` === `${id}`);
      if (opt) {
        return opt.value;
      }
    }
    return '???';
  };

  const getLevelLabelForClose = () => {
    const level = parentOptionIds === undefined || parentOptionIds === null || parentOptionIds.length === 0 ? 2 : parentOptionIds.length + 2;
    return level;
  };

  return (
    <>
      {defaultOption && fieldType ? (
        <>
          {/* CASCADING CHILD OPTIONS */}
          <ModalTransition>
            {isChildOptionsModalOpen && childOptionsModalParentOptionId && (
              <Modal onClose={() => setIsChildOptionsModalOpen(false)} width="large">
                <ModalHeader>
                  <h3>{getOptionNameById(childOptionsModalParentOptionId)}</h3>
                </ModalHeader>
                <ModalBody>
                  <FieldEditorStateful
                    parentOptionIds={parentOptionIds ? [...parentOptionIds, childOptionsModalParentOptionId] : [childOptionsModalParentOptionId]}
                    customFieldId={customFieldId}
                    contextId={contextId}
                    defaultOption={defaultOption}
                    setDefaultOption={setDefaultOption}
                    onOptionDeletedForDefaultOptionUpdate={onOptionDeletedForDefaultOptionUpdate}
                    fieldType={fieldType}
                    authenticatedUserCapabilities={authenticatedUserCapabilities}
                  />
                </ModalBody>
                <ModalFooter>
                  <Button
                    appearance="primary"
                    onClick={() => {
                      setIsChildOptionsModalOpen(false);
                    }}
                  >
                    {modalCloseText + ' ' + getLevelLabelForClose()}
                  </Button>
                </ModalFooter>
              </Modal>
            )}
          </ModalTransition>
          {/* PARENT OPTIONS */}
          <FieldEditor
            customFieldId={customFieldId}
            contextId={contextId}
            parentOptionIds={parentOptionIds}
            onUpdateOption={handleUpdateOption}
            validationErrorFromServer={validationErrorFromServer}
            options={customFieldOptions && customFieldOptions.values ? customFieldOptions.values : []}
            defaultOption={defaultOption}
            defaultOptionEnriched={defaultOptionEnriched}
            fieldType={fieldType}
            isLoading={customFieldOptionsLoadingState === LoadingState.LOADING}
            isDeleting={isDeletingOptionIdOrNull}
            isUpdatingOptionIdOrNull={isUpdatingOptionIdOrNull}
            isSorting={sortOptionsLoadingState === LoadingState.LOADING}
            authenticatedUserCapabilities={authenticatedUserCapabilities}
            allOptionIdsAndValuesLowerCase={allOptionIdsAndValuesLowerCase}
          />
        </>
      ) : null}
    </>
  );
};
